/**
 * Which image belongs on screen after each stage of a run, and whether the
 * viewport is still ours to move.
 *
 * A pipeline that only reveals its work at the end asks the reader to trust six
 * stages at once. Each stage has an image that says whether it went right, and
 * the moment to look at that image is when it appears — a brain mask that ate
 * the temporal lobe is obvious in the mask and invisible in the final lesion
 * volume. So each completed stage retargets the viewport:
 *
 *   prepdata      native DWI, with the reference lesion if the case has one
 *   gen_mask      native DWI under the brain mask it just made
 *   skull_strip   the masked DWI
 *   registration  the DWI in MNI space
 *   inference     DWI with the predicted lesion on top
 *   report        nothing — it writes tables and figures, not volumes
 *
 * Two rules keep this from being hostile. Base and overlay are always chosen
 * from the same space, because NiiVue will happily draw an MNI mask over a
 * native DWI through the affine and the result is a lesion in the wrong place
 * rather than an error. And the moment the reader picks their own image, the
 * following stops for the rest of that run: a viewport that yanks itself away
 * from what someone is examining is worse than one that never moved.
 *
 * Policy only, no store and no IPC, so the rules are testable on their own.
 * The effects live in follow.ts.
 */

import type { CaseManifest, CaseNode, Modality, StageId } from "@/ipc/contract";

export interface OverlayStyle {
  colormap: string;
  opacity: number;
}

export interface StageView {
  base: CaseNode;
  overlay: CaseNode | null;
  style: OverlayStyle | null;
  /** Carried so a test, or a log line, can say which rule fired. */
  why: string;
}

/** A binary prediction: flat red, no ramp to suggest confidence it never gave. */
export const PREDICTION: OverlayStyle = { colormap: "red", opacity: 0.55 };
/** A reference lesion someone drew: distinct from the prediction on purpose. */
export const REFERENCE: OverlayStyle = { colormap: "winter", opacity: 0.5 };
/** A brain mask: faint, because the point is the edge, not the fill. */
export const EXTRACTION: OverlayStyle = { colormap: "cool", opacity: 0.3 };

interface Rule {
  /** Tried in order; the first pattern with a match wins. */
  base: RegExp[];
  overlay?: RegExp[];
  style?: OverlayStyle;
  why: string;
}

/**
 * Patterns are matched against the file name, not the path, and include the
 * leading `_` that separates the subject id from the role — `_DWI\.nii` matches
 * `sub-01_DWI.nii.gz` and not `sub-01_DWI_brain.nii.gz`.
 */
const RULES: Partial<Record<string, Rule>> = {
  "DWI:prepdata": {
    base: [/_DWI\.nii/i],
    overlay: [/_stroke\.nii/i],
    style: REFERENCE,
    why: "raw DWI as the pipeline read it",
  },
  "DWI:gen_mask": {
    base: [/_DWI\.nii/i],
    overlay: [/_DWIbrain-mask\.nii/i],
    style: EXTRACTION,
    why: "the brain mask over the image it was made from",
  },
  "DWI:skull_strip": {
    base: [/_DWI_brain\.nii/i, /_DWI\.nii/i],
    why: "the masked DWI",
  },
  "DWI:registration": {
    base: [/_DWI_space-MNI152_aff\.nii/i, /_DWI_space-MNI152_affsyn\.nii/i],
    why: "the DWI in MNI space",
  },
  "DWI:inference": {
    base: [/_DWI_brain\.nii/i, /_DWI_space-MNI152_aff\.nii/i, /_DWI\.nii/i],
    overlay: [/_stroke-mask\.nii/i, /_stroke-mask_space-MNI152\.nii/i],
    style: PREDICTION,
    why: "the predicted lesion",
  },
  // report writes tables, PDFs and figures. Leaving the view alone is correct.

  "PWI:prepdata": {
    base: [/_PWI\.nii/i, /_DWI\.nii/i],
    why: "the perfusion series as the pipeline read it",
  },
  "PWI:gen_mask": {
    base: [/_PWI\.nii/i, /_DWI\.nii/i],
    overlay: [/brain-mask\.nii/i],
    style: EXTRACTION,
    why: "the brain mask over the image it was made from",
  },
  "PWI:skull_strip": {
    base: [/_PWI_brain\.nii/i, /_DWI_brain\.nii/i],
    why: "the masked series",
  },
  "PWI:gen_ttp": {
    base: [/_TTP\.nii/i],
    why: "the time-to-peak map, the first perfusion image worth reading",
  },
  "PWI:registration": {
    base: [/_DWI_space-MNI152_aff\.nii/i, /_TTP_space-MNI152_aff\.nii/i],
    why: "the DWI in MNI space",
  },
  "PWI:ttpadc_coreg": {
    base: [/_TTP_space-DWI\.nii/i, /_TTP\.nii/i],
    why: "TTP resampled onto the diffusion grid",
  },
  "PWI:inference": {
    base: [/_TTP_space-MNI152_aff\.nii/i, /_DWI_space-MNI152_aff\.nii/i, /_TTP\.nii/i],
    overlay: [/_HP-mask_space-MNI152\.nii/i, /_HP-mask\.nii/i],
    style: PREDICTION,
    why: "the predicted hypoperfusion",
  },
};

/** True when this stage has something to show at all. */
export function hasStageView(modality: Modality, stage: StageId): boolean {
  return Boolean(RULES[`${modality}:${stage}`]);
}

function fileNameOf(node: CaseNode): string {
  const named = node.meta.fileName;
  if (typeof named === "string" && named.length > 0) return named;
  const parts = node.relPath.split(/[\\/]/);
  return parts[parts.length - 1] ?? node.relPath;
}

/**
 * Outputs before inputs, and never the model's normalised copy: `desc-norm` is
 * rescaled for the network's input range and reads as a broken image to a human.
 */
function ranked(nodes: CaseNode[]): CaseNode[] {
  return nodes
    .filter((n) => !/desc-norm/i.test(fileNameOf(n)))
    .slice()
    .sort((a, b) => Number(b.origin === "output") - Number(a.origin === "output"));
}

function firstMatch(nodes: CaseNode[], patterns: RegExp[]): CaseNode | null {
  const pool = ranked(nodes);
  for (const pattern of patterns) {
    const hit = pool.find((n) => pattern.test(fileNameOf(n)));
    if (hit) return hit;
  }
  return null;
}

/**
 * What to put on screen now that this stage has finished, or null when the
 * stage produced nothing to look at or its files are not on disk yet.
 */
export function pickStageView(
  manifest: CaseManifest,
  modality: Modality,
  stage: StageId,
): StageView | null {
  const rule = RULES[`${modality}:${stage}`];
  if (!rule) return null;

  const overlay = rule.overlay
    ? firstMatch([...manifest.masks, ...manifest.images], rule.overlay)
    : null;

  // The overlay decides the space, so the pair can never be mismatched. With no
  // overlay the base is free to come from wherever the pattern found it.
  const images = overlay ? manifest.images.filter((n) => n.space === overlay.space) : manifest.images;
  const base = firstMatch(images, rule.base) ?? (overlay ? firstMatch(manifest.images, rule.base) : null);
  if (!base) return null;

  const paired = overlay && overlay.space === base.space ? overlay : null;
  return {
    base,
    overlay: paired,
    style: paired ? rule.style ?? PREDICTION : null,
    why: rule.why,
  };
}

/* ------------------------------------------------- who owns the viewport --- */

/** The job being followed, and the last base this module set itself. */
let followedJobId: string | null = null;
let lastSetBaseId: string | null = null;
let handedToUser = false;

/** Start following a job. A new job id hands the viewport back to us. */
export function followJob(jobId: string): void {
  if (jobId === followedJobId) return;
  followedJobId = jobId;
  lastSetBaseId = null;
  handedToUser = false;
}

/** Forget everything. For tests, and for closing a case. */
export function resetFollowing(): void {
  followedJobId = null;
  lastSetBaseId = null;
  handedToUser = false;
}

/** Record a base this module set, so a later change can be told apart. */
export function noteApplied(baseId: string): void {
  lastSetBaseId = baseId;
}

/**
 * True while the viewport is still ours to move. Once the reader selects an
 * image, the base no longer matches what we last set, and that is the signal to
 * stop for the rest of the run.
 */
export function stillFollowing(currentBaseId: string | null): boolean {
  if (handedToUser) return false;
  if (lastSetBaseId === null) return true;
  if (currentBaseId === lastSetBaseId) return true;
  handedToUser = true;
  return false;
}
